import React, { FC } from 'react';

import AvailableLanguageLink from '../AvailableLanguageLink';
import { Link } from 'src/components/Inputs';

interface AvailableLanguage {
  href: string;
  label: string;
}

interface Props {
  availableLanguages: AvailableLanguage[];
}

const EnVersion: FC<Props> = ({ availableLanguages }) => {
  return (
    <>
      <h4
        id='biometric-policy-and-release'
        data-magellan-target='biometric-policy-and-release'
      >
        Biometric Policy and Release
      </h4>

      <p>Available languages:</p>
      <ul>
        {availableLanguages.map((language, i) => {
          return (
            <AvailableLanguageLink
              key={`language-${i}`}
              href={language.href}
              label={language.label}
            />
          );
        })}
      </ul>
      <br />
      <br />

      <p>Last Updated: November 11, 2024</p>
      <p>
        Plaid’s Biometric Policy and Release (“Biometric Policy”) describes how
        Plaid Inc. and its subsidiaries, Plaid Financial Ltd. and Plaid, B.V.
        (collectively, “Plaid”) treat the photos and/or video images of your
        face, including scans of facial geometry extracted from those images,
        which may be considered biometric data in some jurisdictions.
      </p>
      <p>
        Plaid acts as a service provider for our customers when providing
        identity verification and fraud prevention services. To provide our
        identity verification and/or fraud prevention services, our customers
        may require you to provide Plaid and/or Plaid’s service providers with
        an identity document that contains your image and/or a photograph or
        video image of yourself.
      </p>
      <p>
        Plaid and/or Plaid’s service providers collect and process the image
        from your identity document and your photograph or video image, at our
        customer’s instruction, to derive a scan of your facial geometry using
        facial recognition or similar technology in order to provide our
        customers with identity verification and fraud prevention services.
        Plaid and/or Plaid’s service providers may also process and use your
        photograph or video image for fraud detection and prevention services,
        including to determine whether your photograph or video image depicts a
        real person. Your identity document image, photograph, or video image,
        and any derived facial geometry scan from those images, may be
        considered biometric information in some jurisdictions.
      </p>
      <p>
        Plaid and/or Plaid’s service providers will collect, process, obtain,
        use, and store your identity document image, photograph, or video image
        for the purpose of:
      </p>
      <ol>
        <li>
          Comparing your derived facial geometry from the image on your identity
          document to your derived facial geometry in the photograph or video
          image that you provide to help verify your identity;
        </li>

        <li>
          To detect and prevent fraud, including by determining your estimated
          age, if applicable, using an image from your identity document, an
          image from a photograph or video image of yourself, and/or your
          derived facial geometry; and{' '}
        </li>

        <li>
          To maintain or provide our services for our customers, including
          assisting our Customers to review identity verification sessions.
        </li>
      </ol>
      <p>
        The image from your identity document, photograph, or video images of
        your face, and your derived facial geometry, if applicable, are stored
        in an encrypted format by Plaid and Plaid’s service providers on behalf
        of Plaid’s customers.
      </p>
      <p>
        PLAID AND PLAID’S SERVICE PROVIDERS NEVER USE THE PERSONAL INFORMATION
        OR IMAGE EXTRACTED FROM YOUR IDENTITY DOCUMENT, PHOTOGRAPH, OR VIDEO
        IMAGE, INCLUDING ANY DERIVED FACIAL GEOMETRY SCAN ASSOCIATED WITH YOUR
        IMAGES, TO ENHANCE, IMPROVE, OR DEVELOP OUR SERVICES.
      </p>
      <p>
        If you do not provide an identity document with an image of your face or
        a photograph and video image of yourself, Plaid and Plaid’s service
        providers will not process your images to derive your facial geometry
        data to compare your identity document to your photograph or video
        image. Plaid will retain a record of your partially completed
        verification process in accordance with customer’s instructions, unless
        otherwise required by law or legal process to delete such data after a
        shorter period of time. For more information, see our{' '}
        <Link href='#idv-privacy-statement'>
          <a>IDV Privacy Statement</a>
        </Link>
        .
      </p>
      <p>
        <strong>Retention and Storage</strong>
      </p>
      <p>
        Where Plaid serves as a service provider, as defined by applicable law,
        our customers instruct Plaid to process, store, retain, or delete your
        identity document and/or photograph or video image. Plaid and Plaid’s
        service providers will permanently destroy and delete any identity
        document and/or photograph or video image in accordance with our
        customer’s instructions. Plaid will store identity documents and/or
        photograph or video images you provide for the duration of the business
        relationship between Plaid and our customer for which the images were
        collected and retained, unless otherwise required by law or legal
        process to delete such data after a shorter period of time.
      </p>
      <p>
        If you have created a Plaid Account subject to the{' '}
        <Link href='https://plaid.com/legal/#plaid-account-terms'>
          <a>Plaid Account Terms</a>
        </Link>{' '}
        and saved your identity verification information, Plaid may store at
        your instruction one or more identity document(s) containing facial
        imagery that you provide. Plaid does not ever process the image from
        these identity documents for any additional purpose unless instructed to
        do so by you, when you verify with a new app or service, nor does Plaid
        ever derive your facial geometry data from stored identity document(s)
        in your Plaid Account. You can learn more about how to delete your
        stored identity document(s) in your Plaid Account by contacting
        privacy@plaid.com or creating a Plaid Portal account by visiting{' '}
        <Link href='https://my.plaid.com/'>
          <a>my.plaid.com</a>
        </Link>
        . Notwithstanding any indication to the contrary in Plaid’s{' '}
        <Link href='https://plaid.com/legal/#end-user-privacy-policy'>
          <a>End User Privacy Policy</a>
        </Link>{' '}
        or the{' '}
        <Link href='https://plaid.com/legal/#plaid-account-terms'>
          <a>Plaid Account Terms</a>
        </Link>{' '}
        this Biometric Policy shall prevail in the event of any inconsistency
        with other terms or notices provided to you.
      </p>
      <p>
        <strong>Additional Terms</strong>
      </p>
      <p>
        Plaid and/or Plaid’s service providers only use and process your image
        from your identity document and/or photograph or video image in
        accordance with this Biometric Policy, and for no other reasons. Plaid
        does not sell biometric information. Plaid is not responsible for the
        policies or practices of its customers. Please see Plaid’s{' '}
        <Link href='#idv-privacy-statement'>
          <a>IDV Privacy Statement</a>
        </Link>{' '}
        to understand how we collect and use your Personal Information on behalf
        of our customers. Plaid encourages you to also review our customers’
        privacy policies and any applicable terms and conditions which may apply
        to their use of your data, some of which may be considered biometric
        data in your region.
      </p>
      <p>
        By using Plaid’s identity verification and fraud prevention services to
        connect to an app or service provided by our customers, you agree that
        any identity documents or photographs and video images you provide to
        verify your identity with our customers are (1) authentic, (2) submitted
        identity documents are your own government-issued identity documents,
        and (3) any submitted identity documents, photographs, or video images
        accurately depict your face and only your face in the photograph or
        video image that you provide.
      </p>
      <p>
        You agree that you have read, understand, and voluntarily consent to
        this Biometric Policy and Release; release in favor of Plaid and Plaid’s
        service providers, any claims related to the collection, processing,
        use, disclosure of, or sharing of your identity document and your
        photograph or video image, including any data derived from these images
        to create a facial geometry scan; and you confirm that you are not
        accessing Plaid’s services in any jurisdiction where those services are
        not permitted by applicable law.
      </p>
      <hr className='section-divider' />
    </>
  );
};

export default EnVersion;
